// Power interruptions during the day, shown under the daily health check.
//
// Several a day is normal, and one is often still running when it is logged —
// so an entry can be left open ("still out") and closed later with one click.
// A DVR with no recording for part of the day is explained by these rows.
import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { useAuth } from '../context/AuthContext';

const hhmm = t => String(t || '').slice(0, 5);
const todayStr = () => new Date().toISOString().slice(0, 10);

// Minutes between two HH:MM times, for the "out for 1h 25m" summary.
function durationText(from, to) {
  if (!from || !to) return null;
  const [fh, fm] = hhmm(from).split(':').map(Number);
  const [th, tm] = hhmm(to).split(':').map(Number);
  const mins = (th * 60 + tm) - (fh * 60 + fm);
  if (!(mins >= 0)) return null;
  const h = Math.floor(mins / 60), m = mins % 60;
  return h ? `${h}h ${m}m` : `${m}m`;
}

// `embedded` is the form shown inside a DVR's health check: one campus, one
// day, no pickers — the context is already settled by the check being open.
export default function PowerLog({ canEdit, dvrs = [], embedded = false,
                                   campusId = null, campusName = '', date: fixedDate = null }) {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [date, setDate] = useState(fixedDate || todayStr());
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const blank = { campus_id:'', dvr_asset_id:'', from_time:'', to_time:'', reason:'',
                  recording_lost:true, remarks:'' };
  const [f, setF] = useState(blank);

  const load = useCallback(() => {
    const params = { date };
    if (campusId) params.campus_id = campusId;
    api.get('/surveillance/power', { params })
       .then(r => setRows(r.data)).catch(() => setRows([]));
  }, [date, campusId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    // Embedded, the campus is fixed — no need to fetch a list to choose from.
    if (embedded) return;
    api.get('/master/campuses').then(r => setCampuses(r.data.filter(c => c.active))).catch(() => {});
  }, [embedded]);

  // Only campuses the person can act on — and if they have exactly one, it is
  // chosen for them rather than making them pick from a list of one.
  const mine = campuses.filter(c =>
    ['admin','it_head','management'].includes(user?.role) || (user?.campus_ids || []).includes(c.id));
  useEffect(() => {
    if (!adding || f.campus_id) return;
    if (campusId) setF(s => ({ ...s, campus_id: campusId }));
    else if (mine.length === 1) setF(s => ({ ...s, campus_id: mine[0].id }));
  }, [adding, mine, f.campus_id, campusId]);

  const save = async () => {
    setBusy(true); setErr('');
    try {
      await api.post('/surveillance/power', { ...f, on_date: date });
      setF(blank); setAdding(false); load();
    } catch (e) { setErr(e.response?.data?.error || 'Could not save'); }
    finally { setBusy(false); }
  };

  const close = async (p, when) => {
    setBusy(true); setErr('');
    try { await api.put(`/surveillance/power/${p.id}`, { to_time: when }); load(); }
    catch (e) { setErr(e.response?.data?.error || 'Could not update'); }
    finally { setBusy(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete the ${hhmm(p.from_time)} power entry for ${p.campus_name}?`)) return;
    await api.delete(`/surveillance/power/${p.id}`).catch(() => {});
    load();
  };

  const open = rows.filter(p => !p.to_time);

  return (
    <div className={embedded ? '' : 'card'}
         style={embedded ? { marginBottom:14, border:'1px solid var(--border)', borderRadius:8, padding:12 }
                         : { marginTop:18 }}>
      <div className="flex-gap" style={{ justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:8 }}>
        <div>
          <b style={{ fontSize:14 }}>⚡ Power interruptions{embedded && campusName ? ` — ${campusName}, today` : ''}</b>
          <div style={{ fontSize:12, color:'var(--text2)', marginTop:2 }}>
            Log each cut as it happens — more than one a day is fine. Leave the restored time
            blank while the power is still off.
          </div>
        </div>
        <div className="flex-gap" style={{ gap:8, alignItems:'center' }}>
          {!embedded && (
            <input type="date" value={date} max={todayStr()} onChange={e => setDate(e.target.value)} />
          )}
          {canEdit && !adding && (
            <button type="button" className="btn btn-primary btn-sm" onClick={() => setAdding(true)}>+ Log a cut</button>
          )}
        </div>
      </div>

      {/* An interruption still running is the thing to act on, so it is said
          plainly rather than left for someone to notice in the list. */}
      {open.length > 0 && (
        <div style={{ marginTop:10, padding:'7px 10px', borderRadius:6, fontSize:12.5,
          background:'rgba(255,153,0,0.12)', border:'1px solid #FF9900', color:'var(--orange)' }}>
          {open.length === 1
            ? `Power still out at ${open[0].campus_name} since ${hhmm(open[0].from_time)}`
            : `${open.length} interruptions still open`}
        </div>
      )}

      {adding && (
        <div style={{ marginTop:12, borderTop:'1px solid var(--border)', paddingTop:12 }}>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(150px, 1fr))', gap:10 }}>
            {!embedded && (
              <div className="form-group" style={{ margin:0 }}><label>Campus *</label>
                <select value={f.campus_id} onChange={e => setF(s => ({ ...s, campus_id:e.target.value, dvr_asset_id:'' }))}>
                  <option value="">— select —</option>
                  {mine.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select></div>
            )}
            <div className="form-group" style={{ margin:0 }}><label>Power off at *</label>
              <input type="time" value={f.from_time}
                     onChange={e => setF(s => ({ ...s, from_time:e.target.value }))} /></div>
            <div className="form-group" style={{ margin:0 }}><label>Restored at</label>
              <input type="time" value={f.to_time}
                     onChange={e => setF(s => ({ ...s, to_time:e.target.value }))} />
              <span style={{ fontSize:11, color:'var(--text2)' }}>Leave blank if still out</span></div>
            <div className="form-group" style={{ margin:0 }}><label>Reason</label>
              <input value={f.reason} onChange={e => setF(s => ({ ...s, reason:e.target.value }))}
                     placeholder="EB outage, generator failure…" /></div>
            {/* Only offered when a single DVR was affected — normally a cut
                takes the whole site and this is left alone. */}
            <div className="form-group" style={{ margin:0 }}><label>One DVR only</label>
              <select value={f.dvr_asset_id} onChange={e => setF(s => ({ ...s, dvr_asset_id:e.target.value }))}>
                <option value="">Whole campus</option>
                {dvrs.filter(d => String(d.campus_id) === String(f.campus_id))
                     .map(d => <option key={d.id} value={d.id}>{d.dvr_name || d.asset_tag}</option>)}
              </select></div>
          </div>
          <label className="opt" style={{ marginTop:8 }}>
            <input type="checkbox" checked={f.recording_lost}
                   onChange={e => setF(s => ({ ...s, recording_lost:e.target.checked }))} />
            <span>Recording was lost during this time</span>
          </label>
          <div className="form-group" style={{ marginTop:8 }}><label>Remarks</label>
            <input value={f.remarks} onChange={e => setF(s => ({ ...s, remarks:e.target.value }))} /></div>
          {err && <div style={{ color:'var(--red)', fontSize:12.5, marginBottom:8 }}>{err}</div>}
          <div className="flex-gap" style={{ gap:8 }}>
            <button type="button" className="btn btn-primary" disabled={busy || !f.campus_id || !f.from_time}
                    onClick={save}>{busy ? '…' : 'Save'}</button>
            <button type="button" className="btn btn-secondary" onClick={() => { setAdding(false); setF(blank); setErr(''); }}>Cancel</button>
          </div>
        </div>
      )}

      {!adding && err && <div style={{ color:'var(--red)', fontSize:12.5, marginTop:8 }}>{err}</div>}

      <div style={{ marginTop:12 }}>
        {!rows.length
          ? <div style={{ fontSize:12.5, color:'var(--text2)' }}>
              No power interruptions recorded for {new Date(date).toLocaleDateString('en-IN')}.
            </div>
          : <div className="table-wrap"><table>
              <thead><tr><th>Campus</th><th>Off</th><th>Restored</th><th>For</th>
                <th>Reason</th><th>Recording</th><th></th></tr></thead>
              <tbody>{rows.map(p => (
                <tr key={p.id} style={{ background: p.to_time ? undefined : 'rgba(255,153,0,0.07)' }}>
                  <td style={{ fontSize:12.5 }}>{p.campus_name}
                    {p.dvr_label && <div style={{ color:'var(--text2)', fontSize:11 }}>{p.dvr_label} only</div>}</td>
                  <td style={{ fontSize:12.5 }} className="mono">{hhmm(p.from_time)}</td>
                  <td style={{ fontSize:12.5 }} className="mono">
                    {p.to_time ? hhmm(p.to_time)
                      : <span style={{ color:'var(--orange)', fontWeight:600 }}>still out</span>}</td>
                  <td style={{ fontSize:12, color:'var(--text2)' }}>
                    {durationText(p.from_time, p.to_time) || '—'}</td>
                  <td style={{ fontSize:12.5 }}>{p.reason || '—'}
                    {p.remarks && <div style={{ color:'var(--text2)', fontSize:11 }}>{p.remarks}</div>}</td>
                  <td style={{ fontSize:12 }}>
                    {p.recording_lost
                      ? <span className="badge badge-high">lost</span>
                      : <span className="badge badge-completed">kept</span>}</td>
                  <td>{canEdit && (
                    <div className="flex-gap" style={{ gap:6 }}>
                      {!p.to_time && (
                        <button type="button" className="btn btn-secondary" style={{ padding:'2px 8px', fontSize:11 }}
                                disabled={busy} onClick={() => close(p, 'now')}>Restored now</button>
                      )}
                      <button type="button" className="btn btn-secondary" style={{ padding:'2px 8px', fontSize:11 }}
                              onClick={() => remove(p)}>delete</button>
                    </div>
                  )}</td>
                </tr>
              ))}</tbody>
            </table></div>}
      </div>
    </div>
  );
}
